<template>
    <div id="whitelist-modal" class="flex justify-center items-center">
        <div @click="closeByBackdrop" class="backdrop"></div>
        <div class="modal-content">
            <div class="modal-header flex justify-between items-center">
                <h1>{{ $t('whitelist_modal_header') }}</h1>
                <div @click="closeModal" class="close-button flex justify-center items-center cursor-pointer">
                    <i class="fas fa-times"></i>
                </div>
            </div>
            <div class="modal-body">
                <form @submit.prevent="submit()" class="flex flex-col justify-center items-center w-full">
                    <div class="flex flex-col items-start w-full">
                        <label class="mb-1">{{ $t('whitelist_modal_label_title') }}</label>
                        <input
                            v-model="listName"
                            v-validate="'required'"
                            :placeholder="$t('whitelist_modal_list_placeholder')"
                            data-vv-validate-on="input"
                            class="name-input w-full"
                            name="title"
                        />
                        <span v-if="errors.has('title')" class="block mt-1 text-sm text-red">
                            <i class="fas fa-exclamation-circle"></i>
                            {{ $t('whitelist_modal_list_name_is_required') }}
                        </span>
                    </div>
                    <div class="flex flex-col items-start w-full mt-4 mb-1">
                        <label>{{ $t('whitelist_modal_label_names') }}</label>
                    </div>
                    <div class="w-full" style="height: 320px; overflow-y: auto">
                        <div
                            v-for="(whitelist, index) in whitelistData"
                            :key="index"
                            class="flex justify-between items-start w-full mb-4"
                        >
                            <div class="flex-grow">
                                <input
                                    v-model="whitelist.name"
                                    v-validate="{
                                        rules: { required: true, unique: [whitelistData, index], max: 15, min: 3 }
                                    }"
                                    :name="'Name' + index"
                                    :placeholder="$t('whitelist_modal_name_placeholder')"
                                    maxlength="15"
                                    data-vv-validate-on="input"
                                    data-vv-as="Name"
                                    class="name-input w-full"
                                />
                                <span v-if="errors.has('Name' + index)" class="block mt-1 text-sm text-red">
                                    <i class="fas fa-exclamation-circle"></i>
                                    {{ errors.first('Name' + index) }}
                                </span>
                            </div>
                            <div class="ml-2">
                                <XButton
                                    @click="removeName(index)"
                                    :disabled="whitelistData.length === 1"
                                    type="button"
                                    class=""
                                    bg-color="#dc3545"
                                >
                                    <i class="fas fa-times"></i>
                                </XButton>
                            </div>
                        </div>
                    </div>
                    <XButton
                        @click="addName()"
                        type="button"
                        class="w-full mt-4"
                        color="#333"
                        bg-color="rgb(247, 248, 250)"
                    >
                        <i class="fas fa-plus"></i>
                        {{ $t('whitelist_modal_button_add_name') }}
                    </XButton>

                    <XButton type="submit" class="w-full mt-4" bg-color="rgb(40, 167, 69)">
                        {{
                            whitelitModalData
                                ? $t('whitelist_modal_button_update')
                                : $t('whitelist_modal_button_create')
                        }}
                    </XButton>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import XButton from '@/components/v2/XButton'
import { Validator } from 'vee-validate'
import popup from '@/lib/popup.js'

export default {
    name: 'WhitelistFormModal',

    components: {
        XButton
    },

    data() {
        return {
            listName: '',
            whitelistData: [
                {
                    name: ''
                }
            ]
        }
    },

    mounted() {
        const rules = [
            {
                name: 'unique',
                params: ['whitelistData', 'index'],
                message: this.$t('whitelist_modal_name_already_exists'),
                validate: (value, { whitelistData, index }) => {
                    const duplicateIndex = whitelistData.findIndex((item, i) => i !== index && item.name === value)
                    return duplicateIndex === -1
                }
            },
            {
                name: 'max',
                params: ['max'],
                message: this.$t('join_name_too_long'),
                validate: (value, { max }) => value.length <= max
            },
            {
                name: 'min',
                params: ['min'],
                message: this.$t('join_name_too_short'),
                validate: (value, { min }) => value.length >= min
            },
            {
                name: 'required',
                params: ['required'],
                message: this.$t('whitelist_modal_name_is_required'),
                validate: value => !!value
            }
        ]

        rules.forEach(rule => {
            Validator.extend(rule.name, {
                getMessage: () => rule.message,
                validate: (value, params) => rule.validate(value, params),
                paramNames: rule.params
            })
        })

        if (this.whitelitModalData) {
            this.whitelistData = JSON.parse(JSON.stringify(this.whitelitModalData.allowed_users))
            this.listName = this.whitelitModalData.name
        }
    },

    computed: {
        ...mapGetters({
            whitelitModalData: 'whitelitModalData'
        })
    },

    methods: {
        closeModal() {
            this.$emit('close')
        },

        closeByBackdrop(e) {
            if (!e.target.classList.contains('backdrop')) {
                return
            }

            this.closeModal()
        },

        removeName(index) {
            this.whitelistData.splice(index, 1)
        },

        addName() {
            this.whitelistData.push({
                name: ''
            })
        },

        async submit() {
            const validateResult = await this.$validator.validate()

            if (!validateResult) return

            try {
                this.$store.commit('toggleMainLoader', true)

                const payload = {
                    name: this.listName,
                    allowed_users: this.whitelistData
                }

                if (this.whitelitModalData) {
                    payload.id = this.whitelitModalData._id.$oid

                    await this.$store.dispatch('updateSchoolWhitelist', payload)

                    this.$emit('submit', 'edit')

                    this.toastSuccess(this.$t('whitelist_modal_update_success'))
                } else {
                    await this.$store.dispatch('createSchoolWhitelist', payload)

                    this.$emit('submit', 'new')

                    this.toastSuccess(this.$t('whitelist_modal_create_success'))
                }

                this.$store.commit('toggleMainLoader', false)

                this.closeModal()
            } catch (err) {
                this.$store.commit('toggleMainLoader', false)
                this.closeModal()

                if (err.response.data.message === 'monthly_create_limit_reached') {
                    popup.alert(this.$t('whitelist_monthly_create_limit_reached'))
                    return
                }

                this.toastError(this.$t('whitelist_modal_create_error'))
            }
        }
    }
}
</script>

<style lang="scss">
#whitelist-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 100;

    .backdrop {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
        height: 100%;
        background-color: #0006;
        z-index: -1;
    }

    .modal-content {
        display: flex;
        flex-direction: column;
        width: 510px;
        min-height: 600px;
        border-radius: 6px;
        color: #333;
        background-color: #fff;
        margin: 0.5rem;

        .modal-header {
            padding: 14px 14px 0 14px;

            h1 {
                margin: 0;
                font-size: 17.5px;
            }

            .close-button {
                width: 28px;
                height: 28px;
                border-radius: 50%;
                color: #6c757d;
                transition: color 0.2s, background-color 0.2s;

                &:hover {
                    color: #343a40;
                    background: #e9ecef;
                }
            }
        }

        .modal-body {
            padding: 14px;

            .name-input {
                height: 40px;
                border-radius: 0.3rem;
                padding: 10.5px 10.5px 10.5px;
                border: 1px solid #ced4da;
                font-size: 14px;
                color: #333;
            }
        }
    }
}
</style>
